import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { GenericPageError } from '@components/core-ui/GenericPageError';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { SelectableMiniTeamCards } from '@components/TeamCards/SelectableMiniTeamCards';
import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import { OrganizationTeamWithMembersResponseDto } from 'app/API';
import { createFetchWorkspaceAvailableTeamsQueryFn } from 'app/queries/workspaces/createFetchWorkspaceAvailableTeamsQueryFn';
import { workspacesKeys } from 'app/queries/workspaces/workspacesKeys';
import { useState } from 'react';

import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './AddTeamToWorkspaceModal/styles/Content';

const EMPTY_TEAMS: OrganizationTeamWithMembersResponseDto[] = [];

type TProps = {
  orgSlug: string;
  workspaceSlug: string;
  onCancel: () => void;
  onSuccess: (selectedTeams: string[]) => void;
};

export const AddTeamToWorkspaceModal = ({ orgSlug, workspaceSlug, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  const availableTeamsQuery = useQuery({
    queryKey: workspacesKeys.availableTeamsBySlugs(orgSlug, workspaceSlug),
    queryFn: createFetchWorkspaceAvailableTeamsQueryFn(call),
    gcTime: 0,
  });
  const availableTeams = availableTeamsQuery.data ?? EMPTY_TEAMS;
  const isLoading = availableTeamsQuery.isLoading;

  const handleSave = async () => {
    onSuccess(selectedTeams);
  };

  return (
    <ModalWrapper width="490px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Assign Teams</ModalTitle>
      <Content>
        {availableTeamsQuery.isError ? <GenericPageError message="Unable to load teams. Please try again." /> : null}
        {!isLoading && !availableTeamsQuery.isError && !availableTeams.length ? (
          <GenericPageError message="No teams available" />
        ) : null}
        {!isLoading && !!availableTeams.length ? (
          <div className="max-h-96 overflow-y-auto">
            <ul className="flex flex-wrap">
              {availableTeams.map((team) => (
                <SelectableMiniTeamCards
                  selectedTeams={selectedTeams}
                  setSelectedTeams={setSelectedTeams}
                  key={team.id}
                  team={team}
                />
              ))}
            </ul>
          </div>
        ) : null}
        <div className="buttonContainer m-auto">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <BoldButton onClick={handleSave}>Add</BoldButton>
        </div>
      </Content>
    </ModalWrapper>
  );
};
